// apps/todo/src/App.jsx
// Listflow — ported into the bnogh monorepo

import { useState, useEffect, createContext, useContext } from "react";
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signInWithPopup,
  GoogleAuthProvider,
  signOut,
  onAuthStateChanged,
  updateProfile,
} from "firebase/auth";
import {
  collection,
  doc,
  setDoc,
  getDoc,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  onSnapshot,
  query,
  where,
  serverTimestamp,
  arrayUnion,
  arrayRemove,
  orderBy,
} from "firebase/firestore";
import { getFunctions, httpsCallable } from "firebase/functions";
import { auth, db } from "./firebase/firebaseConfig";

const functions = getFunctions();
const googleProvider = new GoogleAuthProvider();

// ─── Context ──────────────────────────────────────────────────────────────────
const AppContext = createContext(null);
const useApp = () => useContext(AppContext);

// ─── Constants ────────────────────────────────────────────────────────────────
const FREE_LIST_LIMIT = 2;
const URGENCY_LEVELS = ["low", "medium", "high", "critical"];
const URGENCY_COLORS = {
  low: "#4ade80",
  medium: "#facc15",
  high: "#fb923c",
  critical: "#f87171",
};

// ─── Stripe Hook ──────────────────────────────────────────────────────────────
function useStripe() {
  const upgradeToPro = async () => {
    try {
      const createCheckoutSession = httpsCallable(functions, "createCheckoutSession");
      const { data } = await createCheckoutSession();
      if (data?.url) {
        window.location.href = data.url;
      } else {
        alert("Could not create checkout session. Please try again.");
      }
    } catch (err) {
      console.error("Stripe error:", err);
      alert("Error starting checkout: " + err.message);
    }
  };
  return { upgradeToPro };
}

// ─── App Provider ─────────────────────────────────────────────────────────────
function AppProvider({ children }) {
  const [user, setUser] = useState(null);
  const [userDoc, setUserDoc] = useState(null);
  const [lists, setLists] = useState([]);
  const [activeList, setActiveList] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [authLoading, setAuthLoading] = useState(false);
  const [error, setError] = useState(null);
  const { upgradeToPro } = useStripe();

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (u) => {
      setUser(u);
      if (u) await ensureUserDoc(u);
      else {
        setUserDoc(null);
        setLists([]);
        setActiveList(null);
        setTasks([]);
      }
      setLoading(false);
    });
    return unsub;
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("upgraded") === "true") {
      window.history.replaceState({}, "", window.location.pathname);
      alert("🎉 Welcome to Pro! Your account has been upgraded.");
    }
    if (params.get("canceled") === "true") {
      window.history.replaceState({}, "", window.location.pathname);
    }
  }, []);

  const ensureUserDoc = async (u) => {
    const ref = doc(db, "users", u.uid);
    const snap = await getDoc(ref);
    if (!snap.exists()) {
      await setDoc(ref, {
        uid: u.uid,
        email: u.email,
        displayName: u.displayName || u.email.split("@")[0],
        isPro: false,
        isAdmin: false,
        createdAt: serverTimestamp(),
      });
    }
    onSnapshot(ref, (s) => setUserDoc({ id: s.id, ...s.data() }));
  };

  useEffect(() => {
    if (!user) return;
    const q = query(collection(db, "lists"), where("members", "array-contains", user.uid));
    const unsub = onSnapshot(q, (snap) => {
      const data = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      data.sort((a, b) => (a.createdAt?.seconds || 0) - (b.createdAt?.seconds || 0));
      setLists(data);
    });
    return unsub;
  }, [user]);

  useEffect(() => {
    if (!activeList) { setTasks([]); return; }
    const q = query(collection(db, "lists", activeList.id, "tasks"), orderBy("createdAt", "asc"));
    const unsub = onSnapshot(q, (snap) => {
      setTasks(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    });
    return unsub;
  }, [activeList]);

  const signUp = async (email, password, name) => {
    setAuthLoading(true); setError(null);
    try {
      const cred = await createUserWithEmailAndPassword(auth, email, password);
      await updateProfile(cred.user, { displayName: name });
      await ensureUserDoc({ ...cred.user, displayName: name });
    } catch (e) { setError(e.message); }
    setAuthLoading(false);
  };

  const signIn = async (email, password) => {
    setAuthLoading(true); setError(null);
    try { await signInWithEmailAndPassword(auth, email, password); }
    catch (e) { setError(e.message); }
    setAuthLoading(false);
  };

  const signInGoogle = async () => {
    setAuthLoading(true); setError(null);
    try {
      const result = await signInWithPopup(auth, googleProvider);
      await ensureUserDoc(result.user);
    } catch (e) { setError(e.message); }
    setAuthLoading(false);
  };

  const logOut = () => signOut(auth);

  const canCreateList = () => {
    if (!userDoc) return false;
    if (userDoc.isPro || userDoc.isAdmin) return true;
    return lists.filter((l) => l.ownerId === user.uid).length < FREE_LIST_LIMIT;
  };

  const createList = async (name, description = "") => {
    if (!canCreateList()) return false;
    const ref = await addDoc(collection(db, "lists"), {
      name, description,
      ownerId: user.uid,
      members: [user.uid],
      memberEmails: { [user.uid]: user.email },
      tags: [],
      createdAt: serverTimestamp(),
    });
    setActiveList({ id: ref.id, name, description, ownerId: user.uid, members: [user.uid], tags: [] });
    return true;
  };

  const deleteList = async (listId) => {
    const snap = await getDocs(collection(db, "lists", listId, "tasks"));
    for (const t of snap.docs) await deleteDoc(t.ref);
    await deleteDoc(doc(db, "lists", listId));
    if (activeList?.id === listId) setActiveList(null);
  };

  const updateList = async (listId, data) => {
    await updateDoc(doc(db, "lists", listId), data);
    if (activeList?.id === listId) setActiveList((p) => ({ ...p, ...data }));
  };

  const shareList = async (listId, email) => {
    const snap = await getDocs(query(collection(db, "users"), where("email", "==", email)));
    if (snap.empty) {
      await addDoc(collection(db, "invites"), {
        listId, invitedBy: user.uid, invitedEmail: email,
        status: "pending", createdAt: serverTimestamp(),
      });
      return { status: "invited", message: `Invitation sent to ${email}` };
    }
    const target = { id: snap.docs[0].id, ...snap.docs[0].data() };
    await updateDoc(doc(db, "lists", listId), {
      members: arrayUnion(target.id),
      [`memberEmails.${target.id}`]: email,
    });
    return { status: "added", message: `${email} added to list` };
  };

  const removeFromList = async (listId, memberId) => {
    await updateDoc(doc(db, "lists", listId), { members: arrayRemove(memberId) });
  };

  const addTag = async (listId, tag) => {
    const list = lists.find((l) => l.id === listId);
    if (!list || list.tags?.includes(tag)) return;
    await updateDoc(doc(db, "lists", listId), { tags: arrayUnion(tag) });
  };

  const createTask = async (listId, taskData) => {
    await addDoc(collection(db, "lists", listId, "tasks"), {
      ...taskData, completed: false, createdBy: user.uid, createdAt: serverTimestamp(),
    });
  };

  const updateTask = async (listId, taskId, data) => {
    await updateDoc(doc(db, "lists", listId, "tasks", taskId), data);
  };

  const deleteTask = async (listId, taskId) => {
    await deleteDoc(doc(db, "lists", listId, "tasks", taskId));
  };

  const toggleTask = async (listId, taskId, completed) => {
    await updateDoc(doc(db, "lists", listId, "tasks", taskId), {
      completed: !completed,
      completedAt: !completed ? serverTimestamp() : null,
    });
  };

  const claimAdmin = async () => {
    const snap = await getDocs(query(collection(db, "users"), where("isAdmin", "==", true)));
    if (!snap.empty) return { error: "An admin already exists." };
    await updateDoc(doc(db, "users", user.uid), { isAdmin: true });
    return { success: true };
  };

  const sendInvite = async (email) => {
    const snap = await getDocs(query(collection(db, "users"), where("email", "==", email)));
    if (!snap.empty) return { error: "That email already has an account." };
    const existing = await getDocs(query(
      collection(db, "invites"),
      where("invitedEmail", "==", email),
      where("type", "==", "app"),
      where("status", "==", "pending")
    ));
    if (!existing.empty) return { error: "An invite is already pending for that email." };
    await addDoc(collection(db, "invites"), {
      invitedEmail: email, invitedBy: user.uid,
      type: "app", status: "pending", createdAt: serverTimestamp(),
    });
    return { success: true, message: `Invite sent to ${email}` };
  };

  const getPendingInvites = async () => {
    const snap = await getDocs(query(
      collection(db, "invites"),
      where("type", "==", "app"),
      where("status", "==", "pending")
    ));
    return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
  };

  const revokeInvite = async (inviteId) => {
    await deleteDoc(doc(db, "invites", inviteId));
  };

  const grantPro = async (targetEmail) => {
    const snap = await getDocs(query(collection(db, "users"), where("email", "==", targetEmail)));
    if (snap.empty) return { error: "User not found" };
    await updateDoc(snap.docs[0].ref, { isPro: true });
    return { success: true };
  };

  const getAllUsers = async () => {
    const snap = await getDocs(collection(db, "users"));
    return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
  };

  return (
    <AppContext.Provider value={{
      user, userDoc, lists, activeList, setActiveList, tasks,
      loading, authLoading, error, setError,
      signUp, signIn, signInGoogle, logOut,
      canCreateList, createList, deleteList, updateList, shareList, removeFromList,
      addTag, createTask, updateTask, deleteTask, toggleTask,
      claimAdmin, sendInvite, getPendingInvites, revokeInvite, grantPro, getAllUsers,
      upgradeToPro, FREE_LIST_LIMIT,
    }}>
      {children}
    </AppContext.Provider>
  );
}

// ─── Shared UI ────────────────────────────────────────────────────────────────
const Modal = ({ open, onClose, title, children, wide }) => {
  if (!open) return null;
  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className={`modal-box ${wide ? "modal-wide" : ""}`} onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2 className="modal-title">{title}</h2>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>
        <div className="modal-body">{children}</div>
      </div>
    </div>
  );
};

const Badge = ({ color, children }) => (
  <span className="badge" style={{ background: color + "22", color, borderColor: color + "44" }}>{children}</span>
);

const Input = ({ label, error, ...props }) => (
  <div className="field">
    {label && <label className="field-label">{label}</label>}
    <input className={`field-input ${error ? "field-input--error" : ""}`} {...props} />
    {error && <span className="field-error">{error}</span>}
  </div>
);

const Textarea = ({ label, ...props }) => (
  <div className="field">
    {label && <label className="field-label">{label}</label>}
    <textarea className="field-input field-textarea" {...props} />
  </div>
);

const Select = ({ label, children, ...props }) => (
  <div className="field">
    {label && <label className="field-label">{label}</label>}
    <select className="field-input field-select" {...props}>{children}</select>
  </div>
);

const Btn = ({ variant = "primary", size, loading, children, ...props }) => (
  <button
    className={`btn btn--${variant} ${size ? `btn--${size}` : ""} ${loading ? "btn--loading" : ""}`}
    disabled={loading || props.disabled}
    {...props}
  >
    {loading ? <span className="btn-spinner" /> : children}
  </button>
);

// ─── Auth Page ────────────────────────────────────────────────────────────────
function AuthPage() {
  const { signUp, signIn, signInGoogle, authLoading, error, setError } = useApp();
  const [mode, setMode] = useState("login");
  const [form, setForm] = useState({ name: "", email: "", password: "", confirm: "" });
  const [formError, setFormError] = useState({});
  const set = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));

  const validate = () => {
    const errs = {};
    if (mode === "signup" && !form.name.trim()) errs.name = "Name is required";
    if (!form.email.includes("@")) errs.email = "Valid email required";
    if (form.password.length < 6) errs.password = "Min 6 characters";
    if (mode === "signup" && form.password !== form.confirm) errs.confirm = "Passwords don't match";
    setFormError(errs);
    return Object.keys(errs).length === 0;
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setError(null);
    if (mode === "login") await signIn(form.email, form.password);
    else await signUp(form.email, form.password, form.name);
  };

  return (
    <div className="auth-page">
      <div className="auth-glow" />
      <div className="auth-card">
        <div className="auth-logo">
          <span className="auth-logo-icon">✦</span>
          <span className="auth-logo-text">Listflow</span>
        </div>
        <p className="auth-tagline">Collaborative task management, elevated.</p>
        <button className="google-btn" onClick={signInGoogle} disabled={authLoading}>
          <svg width="18" height="18" viewBox="0 0 24 24">
            <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
            <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
            <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
            <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
          </svg>
          Continue with Google
        </button>
        <div className="auth-divider"><span>or</span></div>
        <div className="auth-tabs">
          <button className={`auth-tab ${mode === "login" ? "active" : ""}`} onClick={() => { setMode("login"); setError(null); }}>Sign In</button>
          <button className={`auth-tab ${mode === "signup" ? "active" : ""}`} onClick={() => { setMode("signup"); setError(null); }}>Create Account</button>
        </div>
        <form onSubmit={submit} className="auth-form">
          {mode === "signup" && <Input label="Full Name" type="text" value={form.name} onChange={set("name")} placeholder="Jane Doe" error={formError.name} />}
          <Input label="Email" type="email" value={form.email} onChange={set("email")} placeholder="you@example.com" error={formError.email} />
          <Input label="Password" type="password" value={form.password} onChange={set("password")} placeholder="••••••••" error={formError.password} />
          {mode === "signup" && <Input label="Confirm Password" type="password" value={form.confirm} onChange={set("confirm")} placeholder="••••••••" error={formError.confirm} />}
          {error && <div className="auth-error">{error}</div>}
          <Btn type="submit" loading={authLoading}>{mode === "login" ? "Sign In" : "Create Account"}</Btn>
        </form>
      </div>
    </div>
  );
}

// ─── Task Form ────────────────────────────────────────────────────────────────
function TaskForm({ listId, tags, task, onDone }) {
  const { createTask, updateTask, addTag } = useApp();
  const [form, setForm] = useState({
    title: task?.title || "",
    description: task?.description || "",
    tag: task?.tag || "",
    urgency: task?.urgency || "medium",
    dueDate: task?.dueDate || "",
    dueTime: task?.dueTime || "",
  });
  const [newTag, setNewTag] = useState("");
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));

  const handleAddTag = async () => {
    if (!newTag.trim()) return;
    await addTag(listId, newTag.trim());
    setForm((p) => ({ ...p, tag: newTag.trim() }));
    setNewTag("");
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) return;
    setSaving(true);
    if (task) await updateTask(listId, task.id, form);
    else await createTask(listId, form);
    setSaving(false);
    onDone();
  };

  return (
    <form onSubmit={submit} className="task-form">
      <Input label="Task Title *" value={form.title} onChange={set("title")} placeholder="What needs to be done?" required />
      <Textarea label="Description" value={form.description} onChange={set("description")} placeholder="Add details, context, or notes..." rows={3} />
      <div className="form-row">
        <div className="field" style={{ flex: 2 }}>
          <label className="field-label">Tag</label>
          <select className="field-input field-select" value={form.tag} onChange={set("tag")}>
            <option value="">No tag</option>
            {tags?.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
        </div>
        <div className="field" style={{ flex: 3 }}>
          <label className="field-label">New Tag</label>
          <div className="tag-input-row">
            <input className="field-input" value={newTag} onChange={(e) => setNewTag(e.target.value)} placeholder="Create tag..." onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), handleAddTag())} />
            <Btn type="button" variant="ghost" size="sm" onClick={handleAddTag}>+ Add</Btn>
          </div>
        </div>
      </div>
      <Select label="Urgency" value={form.urgency} onChange={set("urgency")}>
        {URGENCY_LEVELS.map((u) => <option key={u} value={u}>{u.charAt(0).toUpperCase() + u.slice(1)}</option>)}
      </Select>
      <div className="form-row">
        <Input label="Due Date" type="date" value={form.dueDate} onChange={set("dueDate")} style={{ flex: 1 }} />
        <Input label="Due Time" type="time" value={form.dueTime} onChange={set("dueTime")} style={{ flex: 1 }} />
      </div>
      <div className="form-actions">
        <Btn type="button" variant="ghost" onClick={onDone}>Cancel</Btn>
        <Btn type="submit" loading={saving}>{task ? "Save Changes" : "Create Task"}</Btn>
      </div>
    </form>
  );
}

// ─── Task Card ────────────────────────────────────────────────────────────────
function TaskCard({ task, listId, tags }) {
  const { toggleTask, deleteTask } = useApp();
  const [editing, setEditing] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const isOverdue = task.dueDate && !task.completed && new Date(task.dueDate) < new Date();

  return (
    <>
      <div className={`task-card ${task.completed ? "task-card--done" : ""} ${isOverdue ? "task-card--overdue" : ""}`}>
        <button className={`task-check ${task.completed ? "task-check--done" : ""}`} onClick={() => toggleTask(listId, task.id, task.completed)}>
          {task.completed && "✓"}
        </button>
        <div className="task-body">
          <div className="task-top">
            <span className="task-title">{task.title}</span>
            <div className="task-meta">
              {task.tag && <Badge color="#a78bfa">{task.tag}</Badge>}
              <Badge color={URGENCY_COLORS[task.urgency]}>{task.urgency}</Badge>
              {isOverdue && <Badge color="#f87171">overdue</Badge>}
            </div>
          </div>
          {task.description && <p className="task-desc">{task.description}</p>}
          {(task.dueDate || task.dueTime) && (
            <span className="task-due">📅 {task.dueDate}{task.dueTime && ` at ${task.dueTime}`}</span>
          )}
        </div>
        <div className="task-actions">
          <button className="task-action-btn" onClick={() => setEditing(true)} title="Edit">✎</button>
          <button className="task-action-btn task-action-btn--del" onClick={() => setConfirming(true)} title="Delete">⌫</button>
        </div>
      </div>
      <Modal open={editing} onClose={() => setEditing(false)} title="Edit Task">
        <TaskForm listId={listId} tags={tags} task={task} onDone={() => setEditing(false)} />
      </Modal>
      <Modal open={confirming} onClose={() => setConfirming(false)} title="Delete Task">
        <p style={{ marginBottom: 20, color: "var(--text-2)" }}>Are you sure you want to delete <strong>{task.title}</strong>?</p>
        <div className="form-actions">
          <Btn variant="ghost" onClick={() => setConfirming(false)}>Cancel</Btn>
          <Btn variant="danger" onClick={() => { deleteTask(listId, task.id); setConfirming(false); }}>Delete</Btn>
        </div>
      </Modal>
    </>
  );
}

// ─── List View ────────────────────────────────────────────────────────────────
function ListView() {
  const { activeList, tasks, user, updateList, deleteList, shareList, removeFromList } = useApp();
  const [showAddTask, setShowAddTask] = useState(false);
  const [showShare, setShowShare] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [shareEmail, setShareEmail] = useState("");
  const [shareMsg, setShareMsg] = useState(null);
  const [sortBy, setSortBy] = useState("createdAt");
  const [filterTag, setFilterTag] = useState("all");
  const [filterUrgency, setFilterUrgency] = useState("all");
  const [filterStatus, setFilterStatus] = useState("all");
  const [editName, setEditName] = useState("");
  const [editDesc, setEditDesc] = useState("");
  const [sharing, setSharing] = useState(false);

  useEffect(() => {
    setEditName(activeList?.name || "");
    setEditDesc(activeList?.description || "");
  }, [activeList]);

  if (!activeList) return (
    <div className="empty-state">
      <div className="empty-icon">✦</div>
      <h3>No list selected</h3>
      <p>Create a new list from the sidebar to get started.</p>
    </div>
  );

  const isOwner = activeList.ownerId === user.uid;
  let filtered = [...tasks];
  if (filterTag !== "all") filtered = filtered.filter((t) => t.tag === filterTag);
  if (filterUrgency !== "all") filtered = filtered.filter((t) => t.urgency === filterUrgency);
  if (filterStatus === "active") filtered = filtered.filter((t) => !t.completed);
  if (filterStatus === "done") filtered = filtered.filter((t) => t.completed);
  filtered.sort((a, b) => {
    if (sortBy === "urgency") return URGENCY_LEVELS.indexOf(b.urgency) - URGENCY_LEVELS.indexOf(a.urgency);
    if (sortBy === "dueDate") return (a.dueDate || "9999") < (b.dueDate || "9999") ? -1 : 1;
    if (sortBy === "title") return a.title.localeCompare(b.title);
    if (sortBy === "status") return Number(a.completed) - Number(b.completed);
    return (a.createdAt?.seconds || 0) - (b.createdAt?.seconds || 0);
  });

  const handleShare = async () => {
    if (!shareEmail.trim()) return;
    setSharing(true);
    const result = await shareList(activeList.id, shareEmail.trim());
    setShareMsg(result);
    setShareEmail("");
    setSharing(false);
  };

  const allTags = activeList.tags || [];
  const done = tasks.filter((t) => t.completed).length;
  const pct = tasks.length ? Math.round((done / tasks.length) * 100) : 0;

  return (
    <div className="list-view">
      <div className="list-header">
        <div className="list-header-left">
          <h1 className="list-name">{activeList.name}</h1>
          {activeList.description && <p className="list-desc">{activeList.description}</p>}
        </div>
        <div className="list-header-actions">
          <Btn variant="ghost" size="sm" onClick={() => setShowShare(true)}>Share</Btn>
          {isOwner && <Btn variant="ghost" size="sm" onClick={() => setShowSettings(true)}>Settings</Btn>}
          <Btn size="sm" onClick={() => setShowAddTask(true)}>+ Task</Btn>
        </div>
      </div>
      {tasks.length > 0 && (
        <div className="progress-bar-wrap">
          <div className="progress-bar-track">
            <div className="progress-bar-fill" style={{ width: `${pct}%` }} />
          </div>
          <span className="progress-label">{done}/{tasks.length} complete ({pct}%)</span>
        </div>
      )}
      <div className="filter-bar">
        <select className="filter-select" value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
          <option value="createdAt">Sort: Date Added</option>
          <option value="urgency">Sort: Urgency</option>
          <option value="dueDate">Sort: Due Date</option>
          <option value="title">Sort: A–Z</option>
          <option value="status">Sort: Status</option>
        </select>
        <select className="filter-select" value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
          <option value="all">All Tasks</option>
          <option value="active">Active</option>
          <option value="done">Completed</option>
        </select>
        <select className="filter-select" value={filterUrgency} onChange={(e) => setFilterUrgency(e.target.value)}>
          <option value="all">All Urgency</option>
          {URGENCY_LEVELS.map((u) => <option key={u} value={u}>{u}</option>)}
        </select>
        {allTags.length > 0 && (
          <select className="filter-select" value={filterTag} onChange={(e) => setFilterTag(e.target.value)}>
            <option value="all">All Tags</option>
            {allTags.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
        )}
      </div>
      <div className="tasks-list">
        {filtered.length === 0 ? (
          <div className="tasks-empty"><p>{tasks.length === 0 ? "No tasks yet — add one above!" : "No tasks match your filters."}</p></div>
        ) : (
          filtered.map((t) => <TaskCard key={t.id} task={t} listId={activeList.id} tags={allTags} />)
        )}
      </div>
      <Modal open={showAddTask} onClose={() => setShowAddTask(false)} title="New Task">
        <TaskForm listId={activeList.id} tags={allTags} onDone={() => setShowAddTask(false)} />
      </Modal>
      <Modal open={showShare} onClose={() => { setShowShare(false); setShareMsg(null); }} title="Share List">
        <p className="modal-subtitle">Invite someone to collaborate on <strong>{activeList.name}</strong></p>
        <div className="share-input-row">
          <input className="field-input" value={shareEmail} onChange={(e) => setShareEmail(e.target.value)} placeholder="colleague@email.com" onKeyDown={(e) => e.key === "Enter" && handleShare()} />
          <Btn onClick={handleShare} loading={sharing}>Invite</Btn>
        </div>
        {shareMsg && <div className={`share-msg ${shareMsg.error ? "share-msg--error" : "share-msg--ok"}`}>{shareMsg.error || shareMsg.message}</div>}
        {Object.entries(activeList.memberEmails || {}).length > 0 && (
          <div className="members-list">
            <h4>Members</h4>
            {Object.entries(activeList.memberEmails || {}).map(([uid, email]) => (
              <div key={uid} className="member-row">
                <span className="member-avatar">{email[0].toUpperCase()}</span>
                <span className="member-email">{email}</span>
                <span className="member-role">{uid === activeList.ownerId ? "Owner" : "Member"}</span>
                {isOwner && uid !== user.uid && <button className="member-remove" onClick={() => removeFromList(activeList.id, uid)}>✕</button>}
              </div>
            ))}
          </div>
        )}
      </Modal>
      <Modal open={showSettings} onClose={() => setShowSettings(false)} title="List Settings" wide>
        <div className="settings-section">
          <Input label="List Name" value={editName} onChange={(e) => setEditName(e.target.value)} />
          <Textarea label="Description" value={editDesc} onChange={(e) => setEditDesc(e.target.value)} rows={2} />
          <Btn onClick={async () => { await updateList(activeList.id, { name: editName, description: editDesc }); setShowSettings(false); }}>Save Changes</Btn>
        </div>
        <div className="settings-danger">
          <h4>Danger Zone</h4>
          <Btn variant="danger" onClick={async () => { if (window.confirm("Delete this list and all tasks?")) { await deleteList(activeList.id); setShowSettings(false); } }}>Delete List</Btn>
        </div>
      </Modal>
    </div>
  );
}

// ─── Sidebar ──────────────────────────────────────────────────────────────────
function Sidebar({ onUpgrade, onAdmin }) {
  const { user, userDoc, lists, activeList, setActiveList, canCreateList, createList, logOut, FREE_LIST_LIMIT } = useApp();
  const [showNew, setShowNew] = useState(false);
  const [newName, setNewName] = useState("");
  const [newDesc, setNewDesc] = useState("");
  const [creating, setCreating] = useState(false);
  const ownedCount = lists.filter((l) => l.ownerId === user?.uid).length;

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!newName.trim()) return;
    setCreating(true);
    const ok = await createList(newName.trim(), newDesc.trim());
    setCreating(false);
    if (ok) { setShowNew(false); setNewName(""); setNewDesc(""); }
  };

  return (
    <aside className="sidebar">
      <div className="sidebar-logo">
        <span className="sidebar-logo-icon">✦</span>
        <span className="sidebar-logo-text">Listflow</span>
      </div>
      <div className="sidebar-user">
        <div className="user-avatar">{(userDoc?.displayName || user?.email || "?")[0].toUpperCase()}</div>
        <div className="user-info">
          <span className="user-name">{userDoc?.displayName || user?.email?.split("@")[0]}</span>
          <span className="user-plan">{userDoc?.isAdmin ? "⚡ Admin" : userDoc?.isPro ? "✦ Pro" : "Free"}</span>
        </div>
      </div>
      <div className="sidebar-section">
        <div className="sidebar-section-header">
          <span>My Lists</span>
          {!userDoc?.isPro && !userDoc?.isAdmin && <span className="list-count">{ownedCount}/{FREE_LIST_LIMIT}</span>}
        </div>
        <div className="list-items">
          {lists.map((l) => (
            <button key={l.id} className={`list-item ${activeList?.id === l.id ? "list-item--active" : ""}`} onClick={() => setActiveList(l)}>
              <span className="list-item-dot" />
              <span className="list-item-name">{l.name}</span>
              {l.ownerId !== user?.uid && <span className="list-item-shared">shared</span>}
            </button>
          ))}
        </div>
        {canCreateList() ? (
          showNew ? (
            <form className="new-list-form" onSubmit={handleCreate}>
              <input className="field-input" value={newName} onChange={(e) => setNewName(e.target.value)} placeholder="List name..." autoFocus required />
              <input className="field-input" value={newDesc} onChange={(e) => setNewDesc(e.target.value)} placeholder="Description (optional)" />
              <div className="form-actions">
                <Btn type="button" variant="ghost" size="sm" onClick={() => setShowNew(false)}>Cancel</Btn>
                <Btn type="submit" size="sm" loading={creating}>Create</Btn>
              </div>
            </form>
          ) : (
            <button className="new-list-btn" onClick={() => setShowNew(true)}>+ New List</button>
          )
        ) : (
          <div className="upgrade-nudge">
            <p>You've reached the free limit of {FREE_LIST_LIMIT} lists.</p>
            <Btn size="sm" onClick={onUpgrade}>Upgrade to Pro</Btn>
          </div>
        )}
      </div>
      <div className="sidebar-footer">
        {userDoc?.isAdmin && <button className="sidebar-footer-btn" onClick={onAdmin}>⚡ Admin Panel</button>}
        <a href="https://butnoonegothurt.com" className="sidebar-footer-btn" style={{ textDecoration: "none", display: "block", fontSize: 12, color: "var(--text-3)", padding: "6px 8px" }}>← Back to bnogh</a>
        <button className="sidebar-footer-btn sidebar-signout" onClick={logOut}>Sign Out</button>
      </div>
    </aside>
  );
}

// ─── Upgrade Modal ────────────────────────────────────────────────────────────
function UpgradeModal({ open, onClose }) {
  const { upgradeToPro } = useApp();
  const [loading, setLoading] = useState(false);
  const handleUpgrade = async () => {
    setLoading(true);
    await upgradeToPro();
    setLoading(false);
    onClose();
  };
  return (
    <Modal open={open} onClose={onClose} title="Upgrade to Pro">
      <div className="upgrade-modal">
        <div className="upgrade-price">
          <span className="upgrade-amount">$5</span>
          <span className="upgrade-period">/month</span>
        </div>
        <ul className="upgrade-features">
          <li>✓ Unlimited lists</li>
          <li>✓ Unlimited tasks per list</li>
          <li>✓ Priority support</li>
          <li>✓ Advanced sorting &amp; filtering</li>
          <li>✓ Share with unlimited members</li>
        </ul>
        <Btn onClick={handleUpgrade} loading={loading}>Upgrade via Stripe</Btn>
        <p className="upgrade-note">Secure payment via Stripe. Cancel anytime.</p>
      </div>
    </Modal>
  );
}

// ─── Admin Panel ──────────────────────────────────────────────────────────────
function AdminPanel({ open, onClose }) {
  const { sendInvite, getPendingInvites, revokeInvite, grantPro, getAllUsers } = useApp();
  const [tab, setTab] = useState("invite");
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteMsg, setInviteMsg] = useState(null);
  const [inviting, setInviting] = useState(false);
  const [pendingInvites, setPendingInvites] = useState([]);
  const [users, setUsers] = useState([]);
  const [grantEmail, setGrantEmail] = useState("");
  const [grantMsg, setGrantMsg] = useState(null);
  const [granting, setGranting] = useState(false);

  useEffect(() => {
    if (!open) return;
    getPendingInvites().then(setPendingInvites);
    getAllUsers().then(setUsers);
  }, [open]);

  const handleInvite = async () => {
    if (!inviteEmail.trim()) return;
    setInviting(true);
    const result = await sendInvite(inviteEmail.trim());
    setInviteMsg(result);
    setInviteEmail("");
    if (result.success) getPendingInvites().then(setPendingInvites);
    setInviting(false);
  };

  const handleRevoke = async (id) => {
    await revokeInvite(id);
    getPendingInvites().then(setPendingInvites);
  };

  const handleGrantPro = async () => {
    if (!grantEmail.trim()) return;
    setGranting(true);
    const result = await grantPro(grantEmail.trim());
    setGrantMsg(result.error || "Pro access granted!");
    setGrantEmail("");
    getAllUsers().then(setUsers);
    setGranting(false);
  };

  return (
    <Modal open={open} onClose={onClose} title="⚡ Admin Panel" wide>
      <div className="admin-tabs">
        <button className={`admin-tab ${tab === "invite" ? "active" : ""}`} onClick={() => setTab("invite")}>Invite Users</button>
        <button className={`admin-tab ${tab === "users" ? "active" : ""}`} onClick={() => setTab("users")}>All Users</button>
      </div>
      {tab === "invite" && (
        <>
          <div className="admin-section">
            <h4>Invite Someone to Listflow</h4>
            <p className="admin-hint">Send an invitation to someone who doesn't have an account yet.</p>
            <div className="share-input-row" style={{ marginTop: 12 }}>
              <input className="field-input" value={inviteEmail} onChange={(e) => setInviteEmail(e.target.value)} placeholder="newuser@email.com" onKeyDown={(e) => e.key === "Enter" && handleInvite()} />
              <Btn onClick={handleInvite} loading={inviting}>Send Invite</Btn>
            </div>
            {inviteMsg && <div className={`share-msg ${inviteMsg.error ? "share-msg--error" : "share-msg--ok"}`} style={{ marginTop: 10 }}>{inviteMsg.error || inviteMsg.message}</div>}
          </div>
          <div className="admin-section">
            <h4>Pending Invites ({pendingInvites.length})</h4>
            {pendingInvites.length === 0 ? <p className="admin-hint">No pending invites.</p> : (
              <div className="admin-users">
                {pendingInvites.map((inv) => (
                  <div key={inv.id} className="admin-user-row">
                    <span className="member-avatar">{inv.invitedEmail[0].toUpperCase()}</span>
                    <div className="admin-user-info">
                      <span className="admin-user-email">{inv.invitedEmail}</span>
                    </div>
                    <Badge color="#facc15">pending</Badge>
                    <button className="member-remove" onClick={() => handleRevoke(inv.id)}>✕</button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      )}
      {tab === "users" && (
        <>
          <div className="admin-section">
            <h4>Grant Pro Access</h4>
            <div className="share-input-row">
              <input className="field-input" value={grantEmail} onChange={(e) => setGrantEmail(e.target.value)} placeholder="user@email.com" onKeyDown={(e) => e.key === "Enter" && handleGrantPro()} />
              <Btn onClick={handleGrantPro} loading={granting}>Grant Pro</Btn>
            </div>
            {grantMsg && <div className="share-msg share-msg--ok" style={{ marginTop: 10 }}>{grantMsg}</div>}
          </div>
          <div className="admin-section">
            <h4>All Users ({users.length})</h4>
            <div className="admin-users">
              {users.map((u) => (
                <div key={u.id} className="admin-user-row">
                  <span className="member-avatar">{(u.displayName || u.email || "?")[0].toUpperCase()}</span>
                  <div className="admin-user-info">
                    <span className="admin-user-name">{u.displayName || "—"}</span>
                    <span className="admin-user-email">{u.email}</span>
                  </div>
                  <div className="admin-user-badges">
                    {u.isPro && <Badge color="#a78bfa">Pro</Badge>}
                    {u.isAdmin && <Badge color="#fb923c">Admin</Badge>}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </>
      )}
    </Modal>
  );
}

// ─── Admin Claim Banner ───────────────────────────────────────────────────────
function AdminClaimBanner() {
  const { claimAdmin } = useApp();
  const [claimed, setClaimed] = useState(false);
  const [msg, setMsg] = useState(null);
  const [loading, setLoading] = useState(false);
  if (claimed) return null;
  const handle = async () => {
    setLoading(true);
    const result = await claimAdmin();
    if (result.success) { setMsg("You are now an admin!"); setClaimed(true); }
    else { setMsg(result.error); }
    setLoading(false);
  };
  return (
    <div className="admin-claim-banner">
      <span>🔑 First time setup:</span>
      <Btn size="sm" onClick={handle} loading={loading}>Claim Admin Role</Btn>
      {msg && <span className="admin-claim-msg">{msg}</span>}
    </div>
  );
}

// ─── Main App ─────────────────────────────────────────────────────────────────
function MainApp() {
  const { user, userDoc } = useApp();
  const [showUpgrade, setShowUpgrade] = useState(false);
  const [showAdmin, setShowAdmin] = useState(false);
  if (!user) return <AuthPage />;
  return (
    <div className="app-shell">
      {userDoc && !userDoc.isAdmin && <AdminClaimBanner />}
      <div className="app-layout">
        <Sidebar onUpgrade={() => setShowUpgrade(true)} onAdmin={() => setShowAdmin(true)} />
        <main className="main-content"><ListView /></main>
      </div>
      <UpgradeModal open={showUpgrade} onClose={() => setShowUpgrade(false)} />
      {userDoc?.isAdmin && <AdminPanel open={showAdmin} onClose={() => setShowAdmin(false)} />}
    </div>
  );
}

// ─── Root ─────────────────────────────────────────────────────────────────────
function LoadingScreen() {
  return <div className="loading-screen"><div className="loading-spinner" /></div>;
}

function AppInner() {
  const { loading } = useApp();
  if (loading) return <LoadingScreen />;
  return <MainApp />;
}

export default function App() {
  return <AppProvider><AppInner /></AppProvider>;
}
